"use client";
import Image from "next/image";

import {
  Card,
  CardContent,
  CardFooter,
  CardHeader,
} from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import isCourseType from "@/utils/functions/isCourseType";
import { useLayoutEffect, useRef, useState } from "react";
import { ProductCardProps } from "@/components/ProductCard/types";
import Tags from "@/components/Tags";

const fallbackCardHeader: Record<string, string> = {
  light: "/product-cards/default-card-header-light.png",
  dark: "/product-cards/default-card-header-light.png",
};

const EDX_LOGO: string = "/trademark-logos/edx-logo-registered-elm.png";

export function ProductCardSSRContent({
  name,
  imageUrl,
  type,
  partnerFullName,
  partnerLogoUrl,
  childCoursesCount = 0,
  organizationLogoOverride,
  isNew = false,
}: ProductCardProps) {
  const [imageError, setImageError] = useState(false);
  const theme = isCourseType(type) ? "light" : "dark";
  const cardFallbackImage = fallbackCardHeader[theme];

  // EDX-2719 - images from this host will error
  const cardHeader = imageUrl?.includes("prod-edx-mktg-edit")
    ? cardFallbackImage
    : imageUrl;

  const partnerImage = organizationLogoOverride
    ? organizationLogoOverride
    : partnerLogoUrl
      ? partnerLogoUrl
      : EDX_LOGO;

  const titleRef = useRef<HTMLSpanElement>(null);
  const [titleHeight, setTitleHeight] = useState(0);

  useLayoutEffect(() => {
    if (titleRef.current) {
      const titleHeight = titleRef.current.getBoundingClientRect().height;
      setTitleHeight(titleHeight);
    }
  }, [name]);

  let partnerArray = Array.isArray(partnerFullName)
    ? partnerFullName
    : [partnerFullName];

  if (titleHeight > 60 && partnerArray.length > 2) {
    partnerArray = partnerArray.slice(0, 1);
    partnerArray.push("...");
  }

  return (
    <Card
      className={`${
        theme === "dark" ? "bg-primary" : "bg-white"
      } flex flex-col h-full w-full min-w-[270px]`}
    >
      <CardHeader className="relative rounded-t-xl p-0">
        <Image
          width={270}
          height={104}
          className="m-0 rounded-t-xl object-cover object-top h-[104px] w-auto"
          src={imageError || !cardHeader ? cardFallbackImage : cardHeader}
          alt={name || "Product Image"}
          aria-hidden="true"
          onError={() => setImageError(true)}
        />
        {partnerImage && (
          <Image
            width={116}
            height={66}
            aria-hidden="true"
            className="bg-white shadow absolute p-2 m-0 rounded h-[4.125rem] w-[7.25rem] top-12 left-6 object-scale-down object-center"
            src={partnerImage}
            alt={partnerArray[0] || "Partner Logo"}
          />
        )}
      </CardHeader>
      <CardContent className="mt-7 pb-0">
        <span
          ref={titleRef}
          className={`${
            theme === "dark" ? "text-primary-foreground" : ""
          } font-bold text-base m-0 break-words line-clamp-3`}
        >
          {name}
        </span>
        <div className="flex flex-col w-full">
          {partnerArray?.map((partner, index) => (
            <span
              key={`${partner}-${index}`}
              className={`${
                theme === "dark" ? "text-primary-foreground" : ""
              } text-sm mt-1 truncate`}
            >
              {partner}
            </span>
          ))}
        </div>
      </CardContent>
      <CardFooter className="relative flex flex-col flex-grow gap-2 items-start justify-end px-5 pb-5">
        <div className="flex items-center gap-2">
          <Badge variant={theme === "dark" ? "primaryLight" : "default"}>
            {type}
          </Badge>
        </div>
        {childCoursesCount > 0 && (
          <span
            className={`${theme === "dark" ? "text-primary-foreground" : ""} text-xs pl-2`}
          >{`${childCoursesCount} Courses`}</span>
        )}
        {isNew && (
          <div className="absolute bottom-4 right-4">
            <Tags variant="badges" tags={[{ text: "NEW", color: "orange" }]} />
          </div>
        )}
      </CardFooter>
    </Card>
  );
}
